/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Collapsible } from 'src/core/components/Collapsible';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { ImportModal, ImportModalDto } from 'src/core/components/ImportModal';
import { RenameModal, RenameModalDto } from 'src/core/components/RenameModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { batchBladeMessages } from 'src/luis/projects/testPane/components/BatchBlade/batchBladeMessages';
import { BatchTestInspector } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestInspector/BatchTestInspector';
import { BatchTestTable } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestTable/BatchTestTable';
import { BatchTestTableCommandBar } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestTable/BatchTestTableCommandBar/BatchTestTableCommandBar';
import { TestBladeActionBar } from 'src/luis/projects/testPane/components/common/TestBladeActionBar';
import { getTestPaneTopAction } from 'src/luis/projects/testPane/components/common/TestPaneTopAction';
import { BatchTest } from 'src/luis/projects/testPane/types/BatchTest';

/**
 * The props for the BatchTestBlade
 */
export type BatchBladeProps = {
    /**
     * Called when the user wants to open the single testing panel.
     */
    onOpenSingleTestingPanel: () => void;

    /**
     * Header to be announced by screen reader
     */
    bladeHeader: string;
};

const OpenSingleTestingAction = getTestPaneTopAction('right', 'dark');

/**
 * Renders the batch testing blade.
 */
export const BatchBlade = observer((props: BatchBladeProps) => {
    const { onOpenSingleTestingPanel, bladeHeader } = props;
    const { selection, selectedItems } = useSelection<BatchTest>();
    const { rootStore, dispatcher } = useGlobalState();
    const { intents } = useIntents();
    const { entities } = useEntities();
    const { localize } = useLocalization();
    const [batchTestToInspect, setBatchTestToInspect] = React.useState<BatchTest>(null);
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const {
        currentAppVersionStore: { batchTestStoreHouse },
        isRTL
    } = rootStore.currentLuisAppStore;

    React.useEffect(() => dispatcher.dispatch('loadBatchTests', {}), []);

    const tracker = useProgressTracker({ onComplete: () => selection.setAllSelected(false) });

    /**
     * Imports the batch test dataset.
     *
     * @param dto The dto returned from the import modal.
     */
    const onImportBatchTest = (dto: ImportModalDto) => {
        dispatcher.dispatch('addBatchTest', {
            name: dto.name,
            datasetItemsFile: dto.file,
            toastData: {
                running: { title: localize(batchBladeMessages.importingRunning) },
                completed: { title: localize(batchBladeMessages.importingComplete) },
                failed: { title: localize(batchBladeMessages.importingFailing) }
            }
        });
        setModalsState({ isImportModalHidden: true });
    };

    /**
     * Renames the selected batch test with the given name
     * from the rename modal.
     */
    const onRenameBatchTest = (modalData: RenameModalDto) => {
        const updatedBatchTest = { ...selectedItems[0], name: modalData.name };
        dispatcher.dispatch('renameBatchTest', {
            newBatchTest: updatedBatchTest,
            tracker,
            toastData: {
                running: { title: localize(batchBladeMessages.renamingRunning) },
                completed: { title: localize(batchBladeMessages.renamingComplete) },
                failed: { title: localize(batchBladeMessages.renamingFailing) }
            }
        });
        setModalsState({ isRenameTestModalHidden: true });
    };

    /**
     * Deletes the selected batch test.
     */
    const onDeleteBatchTests = () => {
        dispatcher.dispatch('deleteBatchTests', {
            testIds: selectedItems.map(t => t.id),
            tracker,
            toastData: {
                running: { title: localize(batchBladeMessages.deletingRunning) },
                completed: { title: localize(batchBladeMessages.deletingComplete) },
                failed: { title: localize(batchBladeMessages.deletingFailing) }
            }
        });
        setModalsState({ isDeleteModalHidden: true });
    };

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Announced message={bladeHeader} />

            <TestBladeActionBar>
                <Stack horizontal horizontalAlign="end">
                    <OpenSingleTestingAction
                        ariaLabel={localize(batchBladeMessages.singleTestPanelAriaLabel)}
                        onClick={onOpenSingleTestingPanel}>
                        {localize(batchBladeMessages.singleTestPanel)}
                    </OpenSingleTestingAction>
                </Stack>
            </TestBladeActionBar>

            {batchTestToInspect ? (
                <BatchTestInspector
                    batchTest={batchTestToInspect}
                    intents={intents}
                    entities={entities}
                    onGoBack={() => setBatchTestToInspect(null)}
                    isRTL={isRTL}
                />
            ) : (
                <Stack tokens={{ childrenGap: 10 }}>
                    <BatchTestTableCommandBar
                        selectedItems={selectedItems}
                        onRunBatchTest={() => dispatcher.dispatch('runBatchTest', { testId: selectedItems[0].id })}
                        onRenameBatchTest={() => setModalsState({ isRenameTestModalHidden: false })}
                        onDownloadBatchTest={() =>
                            dispatcher.dispatch('exportBatchTest', { id: selectedItems[0].id, name: selectedItems[0].name })
                        }
                        onDeleteBatchTests={() => setModalsState({ isDeleteModalHidden: false })}
                        onImportBatchTest={() => setModalsState({ isImportModalHidden: false })}
                        userRole={userRole}
                        displayedUserRole={displayedUserRole}
                    />

                    <BatchTestTable
                        items={batchTestStoreHouse.items.slice()}
                        isLoading={batchTestStoreHouse.isLoading}
                        selection={selection}
                        onRunBatchTest={batchTest => dispatcher.dispatch('runBatchTest', { testId: batchTest.id })}
                        onInspectBatchTest={setBatchTestToInspect}
                    />
                </Stack>
            )}

            <ImportModal
                isNameRequired
                onSubmit={onImportBatchTest}
                hidden={modalsState.isImportModalHidden}
                onDismiss={_ => setModalsState({ isImportModalHidden: true })}
                dialogContentProps={{ title: localize(batchBladeMessages.importModalTitle) }}>
                <Collapsible
                    collapsed={modalsState.isImportModalHelperCollapsed}
                    title={localize(batchBladeMessages.importModalSample)}
                    onTitleClick={() => setModalsState({ isImportModalHelperCollapsed: !modalsState.isImportModalHelperCollapsed })}>
                    <code>
                        <pre>{JSON.stringify(sampleJSON, null, 4)}</pre>
                    </code>
                </Collapsible>
            </ImportModal>

            <RenameModal
                onSubmit={onRenameBatchTest}
                hidden={modalsState.isRenameTestModalHidden}
                name={selectedItems[0] && selectedItems[0].name}
                onDismiss={() => setModalsState({ isRenameTestModalHidden: true })}
                dialogContentProps={{ title: localize(batchBladeMessages.renameModalTitle) }}
            />

            <DeletePromptModal
                hidden={modalsState.isDeleteModalHidden}
                onDelete={onDeleteBatchTests}
                dialogContentProps={{
                    title: localize(batchBladeMessages.deleteModalTitle),
                    subText: localize(batchBladeMessages.deleteDatasetBody)
                }}
                onDismiss={() => setModalsState({ isDeleteModalHidden: true })}
            />
        </Stack>
    );
});

const sampleJSON = [{ text: 'hey dad, are you hungry?', intent: 'None', entities: [{ entity: 'FamilyMember', startPos: 4, endPos: 6 }] }];

const defaultModalsState = {
    isImportModalHidden: true,
    isImportModalHelperCollapsed: true,
    isDeleteModalHidden: true,
    isRenameTestModalHidden: true
};
